import React, { useState, useRef, useEffect } from 'react';
import { FaUpload, FaExclamationTriangle, FaCircle } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import Webcam from 'react-webcam';
import moment from 'moment';
import 'moment/locale/zh-tw';
import axios from 'axios';

// Set locale here
moment.locale('zh-tw');

// AI Processing Function
export async function processImageWithAI(apiUrl, imageUrl, thumbnailUrl, setNoClothingDetected, setErrorModalVisible, projectCommand) {
    try {
        const response = await axios.post(apiUrl, {
            command: projectCommand,
            image_url: imageUrl,
            thumbnail_url: thumbnailUrl
        });
        console.log('AI Processing Success:', JSON.stringify(response.data, null, 2));

        if (response.data === null) {
            console.log('無相片被偵測');
            setNoClothingDetected(true);
        }

        return response.data;
    } catch (error) {
        console.error('AI Processing Failed:', error);

        if (error.response && error.response.status === 400) {
            setErrorModalVisible(true);
        }

        return null;
    }
}

// Image Uploader Component
export function ImageUploader({ handlePaste, handleFileChange, handleUploadClick, fileInputRef }) {
    return (
        <div className="row justify-content-center mt-2">
            <div className="col-md-6 col-sm-12 d-flex align-items-center">
                <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => fileInputRef.current.click()}
                >
                    +
                </button>
                <input
                    type="text"
                    className="form-control"
                    placeholder="在此處貼上圖片或拖動圖片"
                    onPaste={handlePaste}
                />
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <button
                    type="button"
                    className="btn btn-success ms-2 d-flex justify-content-center align-items-center"
                    onClick={handleUploadClick}
                    style={{ width: '100px', height: '40px' }}
                >
                    <FaUpload />
                </button>
            </div>
        </div>
    );
}

// Camera View Component
export function CameraView({ webcamRef, videoConstraints, cameraError, setCameraError, handleTakeSnapshot }) {
    return (
        <div className="row justify-content-center mt-4">
            <div className="col-md-6 col-sm-12">
                <div className="position-relative">
                    <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        width="100%"
                        videoConstraints={videoConstraints}
                        onUserMediaError={() => setCameraError(true)}
                        onClick={handleTakeSnapshot}
                        style={{ cursor: 'pointer' }}
                    />
                    {cameraError && (
                        <div className="position-absolute top-50 start-50 translate-middle text-center text-danger fw-bold">
                            相機預覽失敗
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Loading Indicator Component
export function LoadingIndicator() {
    return (
        <div style={{
            position: 'absolute',
            top: '38%',
            left: '50%',
            transform: 'translate(-50%, 0)',
            textAlign: 'center',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '1rem',
            borderRadius: '10px',
            zIndex: 1000,
        }}>
            <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">AI 分析中，請稍候...</p>
        </div>
    );
}

// Results Table Component
export function ResultsTable({
    responseData,
    keyTranslations = {},
    title = "AI分析結果",
    priorityKeys = [],
    excludeKeys = [],
    showWarningIndicator = true,
    onRowClick = null
}) {
    if (!responseData) return null;

    // Function to check if a key-value pair should be highlighted
    const shouldHighlight = (key, value) => {
        if (key === 'dangerous_environment') {
            return value && value !== "無" && value !== "none";
        }
        return priorityKeys.includes(key) && value === true;
    };

    // Check if any priority events are true
    const activeWarnings = Object.entries(responseData)
        .filter(([key, value]) => shouldHighlight(key, value))
        .map(([key, value]) => {
            if (key === 'dangerous_environment') {
                return `${keyTranslations[key] || key}：${value}`;
            }
            return keyTranslations[key] || key;
        });

    return (
        <div className="mt-3">
            {/* Warning Message Banner */}
            {activeWarnings.length > 0 && (
                <div className="alert mb-3 warning-banner">
                    <div className="warning-content">
                        <FaCircle className="warning-icon" />
                        <span className="warning-text">警告：{activeWarnings.join('、')}</span>
                    </div>
                </div>
            )}

            <div className="d-flex align-items-center mb-2">
                <div className="d-flex align-items-center">
                    {showWarningIndicator && (
                        <span className={`ml-6 ${responseData.warning ? "flashing-warning" : ""}`}>
                            {responseData.warning ? (
                                <FaCircle className="text-danger" style={{ fontSize: '1.5em' }} />
                            ) : (
                                <FaCircle className="text-success" style={{ fontSize: '1.5em' }} />
                            )}
                        </span>
                    )}
                    <h5 className="h5 font-weight-bold text-dark mb-0 ms-2">
                        {title}
                    </h5>
                </div>
            </div>

            <div className="table-responsive border rounded">
                <table className="table table-bordered table-hover">
                    <tbody>
                        {Object.entries(responseData)
                            .filter(([key]) => !excludeKeys.includes(key))
                            .sort((a, b) => {
                                const indexA = priorityKeys.indexOf(a[0]);
                                const indexB = priorityKeys.indexOf(b[0]);

                                if (indexA !== -1 && indexB !== -1) {
                                    return indexA - indexB;
                                } else if (indexA !== -1) {
                                    return -1;
                                } else if (indexB !== -1) {
                                    return 1;
                                }
                                return 0;
                            })
                            .map(([key, value], index) => {
                                if (value === '' || value === null || value === undefined) {
                                    return null;
                                }

                                if (key === 'timestamp' && typeof value === 'string') {
                                    value = moment(value).format('YYYY-MM-DD HH:mm:ss');
                                }

                                const isHighlighted = shouldHighlight(key, value);

                                return (
                                    <tr
                                        key={key}
                                        className={`${isHighlighted ? "highlight-row" : index % 2 === 0 ? "table-light" : ""}`}
                                        onClick={onRowClick ? () => onRowClick(key, value) : undefined}
                                        style={onRowClick ? { cursor: 'pointer' } : {}}
                                    >
                                        <td className={`px-4 py-3 align-top font-weight-medium ${isHighlighted ? "highlight-text" : "text-dark"}`}>
                                            {keyTranslations[key] || key}
                                        </td>
                                        <td className={`px-4 py-3 ${isHighlighted ? "highlight-text" : ""}`}>
                                            {key === 'image_url' || key === 'thumbnail_url' || (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) ? (
                                                <div className="d-flex justify-content-center">
                                                    <img src={value} alt="Content"
                                                        className={`rounded border ${key === 'thumbnail_url' ? 'img-thumbnail' : 'img-fluid'} h-auto`} />
                                                </div>
                                            ) : Array.isArray(value) ? (
                                                value.join(', ')
                                            ) : typeof value === 'boolean' ? (
                                                value ? "是" : "否"
                                            ) : typeof value === 'object' ? (
                                                JSON.stringify(value)
                                            ) : (
                                                value
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// Warning conditions message component
export function WarningConditions({ siteType, warningItems }) {
    return (
        <div className="alert alert-info mt-0 mb-0 text-start py-1">
            <h6 className="mb-0">『{siteType}』以下情況將會顯示警告：</h6>
            <ul className="mb-0" style={{ paddingLeft: '12px', marginTop: '1px' }}>
                {warningItems.map(item => (
                    <li key={item.key} style={{ lineHeight: '1.2' }}>{item.label}</li>
                ))}
            </ul>
        </div>
    );
}

// Error Modal Component
export function ErrorModal({ show, onClose }) {
    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            dialogClassName="custom-modal-dialog"
        >
            <Modal.Body className="custom-modal-body d-flex justify-content-center align-items-center p-0 position-relative">
                <button
                    onClick={onClose}
                    className="close-button"
                >
                    &times;
                </button>
                <div style={{
                    textAlign: 'center',
                    color: 'red',
                    fontSize: '1.5rem',
                    padding: '1rem',
                    width: '100%',
                    maxWidth: '500px'
                }}>
                    <FaExclamationTriangle style={{ fontSize: '3rem', color: 'red' }} />
                    <p className="mt-2">圖片無效，請上傳正確的圖片。</p>
                    <button type="button" className="btn btn-danger" onClick={onClose}>重新上傳</button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Success Modal Component
export function SuccessModal({ show, orderId, onClose }) {
    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">訂單刪除成功</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p>訂單號碼 {orderId} 已成功刪除。</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-primary" onClick={onClose}>確定</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// No Clothing Detected Component
export function NoClothingDetected() {
    return (
        <div className="text-danger fw-bold mt-4">
            無衣物被偵測或無法分析
        </div>
    );
}

// Image Display Component
export function ImageDisplay({ imageSrc, hasResults }) {
    return (
        <div className="d-flex justify-content-center">
            <img
                src={imageSrc}
                alt="已貼上的照片"
                className={`img-thumbnail result-image ${hasResults ? 'with-results' : ''}`}
            />
        </div>
    );
}

// Image Processing Functions
export async function handleImageProcessing(image, uploadUrl) {
    try {
        const filename = new Date().toISOString() + '.jpg';

        // Check if we have a valid image
        if (!image) {
            console.error('No image provided for processing');
            throw new Error('No image provided');
        }

        // Convert image to blob
        let blob;
        try {
            blob = await fetch(image).then(res => res.blob());
        } catch (error) {
            console.error('Failed to convert image to blob:', error);
            throw new Error('Failed to process image data');
        }

        // Create and send form data
        const formData = new FormData();
        formData.append('file', blob, filename);

        // Log upload attempt
        console.log('Attempting to upload image to:', uploadUrl);

        // Send the request with better error handling
        try {
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                console.error(`Upload failed with status ${response.status}: ${errorText}`);
                throw new Error(`檔案上傳失敗 (${response.status})`);
            }

            const data = await response.json();
            console.log('檔案上傳成功:', data);

            // Validate response data
            if (!data || !data.imgUrl) {
                console.error('Invalid response data:', data);
                throw new Error('伺服器回應無效');
            }

            return data;

        } catch (fetchError) {
            console.error('Fetch operation failed:', fetchError);
            if (fetchError.message.includes('Failed to fetch')) {
                throw new Error('無法連接到伺服器，請檢查網路連線');
            }
            throw fetchError;
        }
    } catch (error) {
        console.error('Image processing failed:', error);
        throw error;
    }
}

// Main Construction Logic Component
export function EventUploadComponent({
    keyTranslations = {},
    PROJECT_POST_API,
    SITE_TYPE,
    mediaUploadUrl,
    tableTitle = "AI分析結果",
    priorityKeys,
    excludeKeys,
    showWarningIndicator = true,
    onResultRowClick = null,
    ResultsTableComponent = null,
    onResultsDisplay = null,
    configData = {
        excludeKeys: ['id', 'time'],
        defaultVideoConstraints: { facingMode: 'user' },
        mobileDeviceRegex: /iPhone|iPad|iPod|Android/i,
        mobileVideoConstraints: { facingMode: { exact: 'environment' } }
    }
}) {
    // State management
    const [isCameraOpen, setIsCameraOpen] = useState(true);
    const [uploadedImagePath, setUploadedImagePath] = useState('');
    const [responseData, setResponseData] = useState(null);
    const [cameraError, setCameraError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [noClothingDetected, setNoClothingDetected] = useState(false);
    const [videoConstraints, setVideoConstraints] = useState({ facingMode: 'user' });
    const [pastedImage, setPastedImage] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [deletedOrderId, setDeletedOrderId] = useState(null);
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const webcamRef = useRef(null);
    const fileInputRef = useRef(null);

    // Environment detection for camera mode
    useEffect(() => {
        setUploadedImagePath('');
        setNoClothingDetected(false);

        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
            setVideoConstraints({ facingMode: { exact: 'environment' } });
        } else {
            setVideoConstraints({ facingMode: 'user' });
        }
    }, []);

    // Reset function
    const clearAllDataExceptImage = () => {
        setResponseData(null);
        setNoClothingDetected(false);
        setIsCameraOpen(false);
        setModalVisible(false);
        setDeletedOrderId(null);
        setCameraError(false);
    };

    // Modal handlers
    const closeErrorModal = () => {
        setErrorModalVisible(false);
        window.location.reload();
    };

    const closeModal = () => {
        setModalVisible(false);
        setDeletedOrderId(null);
        window.location.reload();
    };

    // Event handlers
    const handleTakeSnapshot = async () => {
        clearAllDataExceptImage();

        if (webcamRef.current) {
            const image = webcamRef.current.getScreenshot();
            if (image) {
                setPastedImage(image);
                setIsCameraOpen(false);
                await handleImageUpload(image);
            } else {
                console.error('Failed to capture the screenshot from the webcam.');
            }
        }
    };

    const handleUploadClick = async () => {
        clearAllDataExceptImage();

        if (pastedImage) {
            await handleImageUpload(pastedImage);
        } else if (isCameraOpen && webcamRef.current) {
            const image = webcamRef.current.getScreenshot();
            if (image) {
                setPastedImage(image);
                setIsCameraOpen(false);
                await handleImageUpload(image);
            }
        }
    };

    const handleFileChange = async (event) => {
        clearAllDataExceptImage();
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPastedImage(reader.result);
                setIsCameraOpen(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePaste = async (event) => {
        clearAllDataExceptImage();
        const items = event.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPastedImage(reader.result);
                    setIsCameraOpen(false);
                };
                reader.readAsDataURL(blob);
            }
        }
    };

    // Image upload function
    const handleImageUpload = async (image) => {
        setLoading(true);
        try {
            console.log('Starting image upload process...');

            if (!image) {
                throw new Error('No image data available');
            }

            if (!mediaUploadUrl) {
                throw new Error('Upload URL not configured');
            }

            console.log('Uploading image to server...');
            const result = await handleImageProcessing(image, mediaUploadUrl);

            if (!result || !result.imgUrl) {
                throw new Error('Missing image URL in server response');
            }

            setUploadedImagePath(result.imgUrl);
            console.log('Image upload successful:', result.imgUrl);

            console.log('Processing with AI...', PROJECT_POST_API);
            const aiResponse = await processImageWithAI(
                PROJECT_POST_API,
                result.imgUrl,
                result.thumbnailUrl || '',
                setNoClothingDetected,
                setErrorModalVisible,
                configData.projectCommand || "image_ai"
            );

            setResponseData(aiResponse);
            if (onResultsDisplay) {
                onResultsDisplay(true);
            }
            console.log('AI processing complete');
        } catch (error) {
            console.error('處理失敗:', error);
            setErrorModalVisible(true);
            // Use a more user-friendly error message
            const errorMessage = error.message || 'Unknown error occurred';
            alert(`處理失敗: ${errorMessage}`);
            if (onResultsDisplay) {
                onResultsDisplay(false);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container text-center mt-1 position-relative">
            {/* <h1 className="position-relative">【 {SITE_TYPE} 】AI</h1> */}

            {!responseData && (
                <ImageUploader
                    handlePaste={handlePaste}
                    handleFileChange={handleFileChange}
                    handleUploadClick={handleUploadClick}
                    fileInputRef={fileInputRef}
                />
            )}

            {pastedImage && <ImageDisplay imageSrc={pastedImage} hasResults={!!responseData} />}

            {!responseData && isCameraOpen && !uploadedImagePath && (
                <CameraView
                    webcamRef={webcamRef}
                    videoConstraints={videoConstraints}
                    cameraError={cameraError}
                    setCameraError={setCameraError}
                    handleTakeSnapshot={handleTakeSnapshot}
                />
            )}

            {loading && <LoadingIndicator />}

            {noClothingDetected && !loading && <NoClothingDetected />}

            {responseData && ResultsTableComponent && (
                <ResultsTableComponent
                    responseData={responseData}
                    keyTranslations={keyTranslations}
                    title={tableTitle}
                    priorityKeys={priorityKeys || configData.priorityKeys}
                    excludeKeys={excludeKeys || configData.excludeKeys}
                    showWarningIndicator={showWarningIndicator}
                    onRowClick={onResultRowClick}
                />
            )}

            {/* Fall back to basic display if no ResultsTableComponent is provided */}
            {responseData && !ResultsTableComponent && (
                <div className="mt-4 p-3 border">
                    <h5>Results Data</h5>
                    <pre className="text-left bg-light p-2">
                        {JSON.stringify(responseData, null, 2)}
                    </pre>
                </div>
            )}

            <SuccessModal
                show={modalVisible}
                orderId={deletedOrderId}
                onClose={closeModal}
            />

            <ErrorModal
                show={errorModalVisible}
                onClose={closeErrorModal}
            />
        </div>
    );
}

// For backward compatibility
export { EventUploadComponent as ConstructionLogic }; 